/**
 * Hook for fetching and managing Vehicle Scene Timeline data
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  VehicleSceneTimelineResponse,
  GraphExplainResponse,
  TimelineControls,
  TimelineSelection,
  TimelineHover,
} from '../types/vehicleSceneTimeline';
import {
  fetchVehicleSceneTimeline,
  fetchGraphExplain,
  fetchVehicleList,
  type VehicleListItem,
} from '../services/truthLayerApi';
import {
  toMs,
  findOverlappingSegments,
  findOverlappingSlices,
  findOverlappingEdges,
  precomputeSegments,
  precomputeEdges,
  precomputeSlices,
  type PrecomputedItem,
  type ZoomPanState,
} from '../utils/timelineUtils';

export interface UseVehicleSceneTimelineReturn {
  // Data
  data: VehicleSceneTimelineResponse | null;
  loading: boolean;
  error: string | null;
  // Vehicle list
  vehicleList: VehicleListItem[];
  vehicleListLoading: boolean;
  // Precomputed ms caches
  segmentCache: PrecomputedItem[];
  orderCache: PrecomputedItem[];
  driverCache: PrecomputedItem[];
  fuelCache: PrecomputedItem[];
  sliceCache: PrecomputedItem[];
  // Controls
  controls: TimelineControls;
  setControls: React.Dispatch<React.SetStateAction<TimelineControls>>;
  // Zoom/pan
  zoomPan: ZoomPanState;
  setZoomPan: React.Dispatch<React.SetStateAction<ZoomPanState>>;
  // Selection
  selection: TimelineSelection | null;
  setSelection: (sel: TimelineSelection | null) => void;
  selectSegment: (segmentId: string) => void;
  selectOrder: (edgeId: string) => void;
  selectDriver: (edgeId: string) => void;
  selectFuel: (edgeId: string) => void;
  selectSlice: (sliceId: string) => void;
  // Hover
  hover: TimelineHover | null;
  setHover: (h: TimelineHover | null) => void;
  // Explain
  explainData: GraphExplainResponse | null;
  explainLoading: boolean;
  explainError: string | null;
  explainEdge: (edgeId: string) => void;
  // Scrubber
  scrubberMs: number | null;
  setScrubberMs: (ms: number | null) => void;
  // Refresh
  refresh: () => void;
}

const defaultControls: TimelineControls = {
  vehicleId: 'vehicle-001',
  from: new Date(Date.now() - 10 * 3600_000).toISOString(),
  to: new Date().toISOString(),
  asOf: null,
  viewMode: 'STACKED',
  lanes: {
    movement: true,
    orders: true,
    drivers: true,
    fuel: true,
    attribution: true,
  },
  confidenceThreshold: 0,
  playheadEnabled: true,
};

export const useVehicleSceneTimeline = (): UseVehicleSceneTimelineReturn => {
  const [data, setData] = useState<VehicleSceneTimelineResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [controls, setControls] = useState<TimelineControls>(defaultControls);
  const [zoomPan, setZoomPan] = useState<ZoomPanState>({ zoom: 1, offsetMs: 0 });
  const [selection, setSelectionRaw] = useState<TimelineSelection | null>(null);
  const [hover, setHover] = useState<TimelineHover | null>(null);
  const [explainData, setExplainData] = useState<GraphExplainResponse | null>(null);
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainError, setExplainError] = useState<string | null>(null);
  const [scrubberMs, setScrubberMs] = useState<number | null>(null);
  const [vehicleList, setVehicleList] = useState<VehicleListItem[]>([]);
  const [vehicleListLoading, setVehicleListLoading] = useState(false);

  // Fetch vehicle list on mount
  useEffect(() => {
    let cancelled = false;
    setVehicleListLoading(true);
    fetchVehicleList()
      .then(list => {
        if (cancelled) return;
        setVehicleList(list);
        // Auto-select first vehicle if current selection isn't in the list
        if (list.length > 0) {
          setControls(prev => {
            const exists = list.some(v => v.id === prev.vehicleId);
            if (!exists) return { ...prev, vehicleId: list[0].id };
            return prev;
          });
        }
      })
      .finally(() => {
        if (!cancelled) setVehicleListLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchVehicleSceneTimeline(
        controls.vehicleId,
        controls.from,
        controls.to,
        controls.asOf,
      );
      setData(result);
      // Reset zoom/pan on data change
      setZoomPan({ zoom: 1, offsetMs: 0 });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch timeline');
    } finally {
      setLoading(false);
    }
  }, [controls.vehicleId, controls.from, controls.to, controls.asOf]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Precompute ms caches
  const segmentCache = useMemo(
    () => (data ? precomputeSegments(data.segments) : []),
    [data],
  );
  const orderCache = useMemo(
    () => (data ? precomputeEdges(data.overlays.orders) : []),
    [data],
  );
  const driverCache = useMemo(
    () => (data ? precomputeEdges(data.overlays.drivers) : []),
    [data],
  );
  const fuelCache = useMemo(
    () => (data ? precomputeEdges(data.overlays.fuel) : []),
    [data],
  );
  const sliceCache = useMemo(
    () => (data ? precomputeSlices(data.attribution_slices) : []),
    [data],
  );

  // Filter by confidence threshold
  const filteredData = useMemo(() => {
    if (!data) return null;
    const threshold = controls.confidenceThreshold;
    if (threshold <= 0) return data;
    return {
      ...data,
      overlays: {
        orders: data.overlays.orders.filter(o => o.confidence >= threshold),
        planned_orders: (data.overlays.planned_orders ?? []).filter(p => p.confidence >= threshold),
        deliveries: (data.overlays.deliveries ?? []).filter(d => d.confidence >= threshold),
        drivers: data.overlays.drivers.filter(d => d.confidence >= threshold),
        fuel: data.overlays.fuel.filter(f => f.confidence >= threshold),
      },
      attribution_slices: data.attribution_slices.filter(
        s => s.confidence >= threshold,
      ),
    };
  }, [data, controls.confidenceThreshold]);

  // Selection with overlap computation
  const setSelection = useCallback(
    (sel: TimelineSelection | null) => {
      if (!sel || !data) {
        setSelectionRaw(null);
        return;
      }

      let fromMs: number;
      let toMs_: number;

      if (sel.type === 'order' || sel.type === 'driver' || sel.type === 'fuel') {
        const allEdges = [
          ...data.overlays.orders,
          ...(data.overlays.planned_orders ?? []),
          ...data.overlays.drivers,
          ...data.overlays.fuel,
        ];
        const edge = allEdges.find(e => e.edge_id === sel.id);
        if (!edge) { setSelectionRaw(null); return; }
        fromMs = toMs(edge.from_ts);
        toMs_ = toMs(edge.to_ts);
        setSelectionRaw({
          ...sel,
          edge,
          relatedSegmentIds: findOverlappingSegments(data.segments, fromMs, toMs_),
          relatedSliceIds: findOverlappingSlices(data.attribution_slices, fromMs, toMs_),
          relatedOrderIds: sel.type === 'order'
            ? findOverlappingEdges(data.overlays.orders, fromMs, toMs_)
            : undefined,
        });
      } else if (sel.type === 'segment') {
        const seg = data.segments.find(s => s.segment_id === sel.id);
        if (!seg) { setSelectionRaw(null); return; }
        fromMs = toMs(seg.start_ts);
        toMs_ = toMs(seg.end_ts);
        setSelectionRaw({
          ...sel,
          segment: seg,
          relatedOrderIds: findOverlappingEdges(data.overlays.orders, fromMs, toMs_),
          relatedDriverIds: findOverlappingEdges(data.overlays.drivers, fromMs, toMs_),
          relatedFuelIds: findOverlappingEdges(data.overlays.fuel, fromMs, toMs_),
          relatedSliceIds: findOverlappingSlices(data.attribution_slices, fromMs, toMs_),
        });
      } else if (sel.type === 'slice') {
        const slice = data.attribution_slices.find(s => s.slice_id === sel.id);
        if (!slice) { setSelectionRaw(null); return; }
        fromMs = toMs(slice.start_ts);
        toMs_ = toMs(slice.end_ts);
        setSelectionRaw({
          ...sel,
          slice,
          relatedSegmentIds: slice.movement_segment_ids,
          relatedOrderIds: slice.active_order_ids,
          relatedDriverIds: slice.active_driver_id ? [slice.active_driver_id] : [],
          relatedFuelIds: slice.active_fuel_ids,
        });
      }
    },
    [data],
  );

  // Shorthand selection helpers
  const selectSegment = useCallback(
    (id: string) => setSelection({ type: 'segment', id }),
    [setSelection],
  );
  const selectOrder = useCallback(
    (id: string) => setSelection({ type: 'order', id }),
    [setSelection],
  );
  const selectDriver = useCallback(
    (id: string) => setSelection({ type: 'driver', id }),
    [setSelection],
  );
  const selectFuel = useCallback(
    (id: string) => setSelection({ type: 'fuel', id }),
    [setSelection],
  );
  const selectSlice = useCallback(
    (id: string) => setSelection({ type: 'slice', id }),
    [setSelection],
  );

  // Explain edge
  const explainEdge = useCallback(async (edgeId: string) => {
    setExplainLoading(true);
    setExplainError(null);
    setExplainData(null);
    try {
      const result = await fetchGraphExplain(edgeId, controls.from, controls.to);
      setExplainData(result);
    } catch (e) {
      setExplainError(e instanceof Error ? e.message : 'Failed to load edge details');
    } finally {
      setExplainLoading(false);
    }
  }, [controls.from, controls.to]);

  return {
    data: filteredData,
    loading,
    error,
    vehicleList,
    vehicleListLoading,
    segmentCache,
    orderCache,
    driverCache,
    fuelCache,
    sliceCache,
    controls,
    setControls,
    zoomPan,
    setZoomPan,
    selection,
    setSelection,
    selectSegment,
    selectOrder,
    selectDriver,
    selectFuel,
    selectSlice,
    hover,
    setHover,
    explainData,
    explainLoading,
    explainError,
    explainEdge,
    scrubberMs,
    setScrubberMs,
    refresh: fetchData,
  };
};
